import { apiClient } from './api';
const businessPath = (providerId?: number) => {
  if (providerId === undefined) return '/api/v1/web/providers/me';
  if (!Number.isSafeInteger(providerId) || providerId <= 0) throw new Error('Invalid business.');
  return `/api/v1/web/provider-businesses/${providerId}`;
};
export async function getMyProviderBusinesses(): Promise<ProviderProfile[]> {
  return (await apiClient.get<ProviderProfile[]>('/api/v1/web/provider-businesses')).data;
}

import type {
  CreateProviderProfilePayload,
  ProviderContactMethod,
  ProviderAcademyDetails,
  ProviderAnalytics,
  ProviderCoachDetails,
  ProviderProfile,
  ProviderReviewItem,
  ProviderReviewPage,
  ProviderSearchResponse,
  ReportProviderReviewPayload,
  SaveProviderReviewPayload,
  ProviderStringerDetails,
  SearchProvidersPayload,
  TenantProgramPublicRegistrationPayload,
  TenantProgramRegistration,
  UpdateProviderProfilePayload,
} from '../types';

export async function searchProviders(
  params: SearchProvidersPayload
): Promise<ProviderSearchResponse> {
  const queryParams = new URLSearchParams();
  queryParams.set('type', params.type);
  if (params.zipcode) queryParams.set('zipcode', params.zipcode);
  if (params.sport) queryParams.set('sport', params.sport);
  if (params.page !== undefined) queryParams.set('page', String(params.page));
  if (params.limit !== undefined) queryParams.set('limit', String(params.limit));

  const response = await apiClient.get<ProviderSearchResponse>(
    `/api/v1/web/providers/search?${queryParams.toString()}`
  );
  return response.data;
}

export async function getProviderProfile(providerId: number): Promise<ProviderProfile> {
  const response = await apiClient.get<ProviderProfile>(`/api/v1/web/providers/${providerId}`);
  return response.data;
}

export async function getMyProviderProfile(providerId?: number): Promise<ProviderProfile | null> {
  const response = await apiClient.get<ProviderProfile>(businessPath(providerId), {
    validateStatus: (status) => status === 200 || status === 204,
  });
  if (response.status === 204) {
    return null;
  }
  return response.data;
}

export async function createProviderProfile(
  payload: CreateProviderProfilePayload, requestId?: string
): Promise<ProviderProfile> {
  const response = await apiClient.post<ProviderProfile>(requestId ? '/api/v1/web/provider-businesses' : businessPath(), payload, requestId ? { headers: { 'Idempotency-Key': requestId } } : undefined);
  return response.data;
}

export async function updateProviderProfile(
  payload: UpdateProviderProfilePayload, providerId?: number
): Promise<ProviderProfile> {
  const response = await apiClient.patch<ProviderProfile>(businessPath(providerId), payload);
  return response.data;
}

export async function uploadProviderProfilePhoto(file: File, providerId?: number): Promise<ProviderProfile> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<ProviderProfile>(
    providerId === undefined ? '/api/v1/web/providers/me/profile-photo' : `${businessPath(providerId)}/photo`,
    formData,
    {
      timeout: 60000,
    }
  );
  return response.data;
}

export async function requestCoachWorkspace(requestNotes?: string, providerId?: number): Promise<ProviderProfile> {
  const response = await apiClient.post<ProviderProfile>(`${businessPath(providerId)}/workspace-request`, {
    requestNotes,
  });
  return response.data;
}

export async function updateStringerDetails(
  payload: ProviderStringerDetails, providerId?: number
): Promise<void> {
  await apiClient.patch(`${businessPath(providerId)}/stringer`, payload);
}

export async function updateCoachDetails(
  payload: ProviderCoachDetails, providerId?: number
): Promise<void> {
  await apiClient.patch(`${businessPath(providerId)}/coach`, payload);
}

export async function updateAcademyDetails(
  payload: ProviderAcademyDetails, providerId?: number
): Promise<void> {
  await apiClient.patch(`${businessPath(providerId)}/academy`, payload);
}

export async function agreeToProviderTerms(
  termsVersion: string, providerId?: number
): Promise<void> {
  await apiClient.post(`${businessPath(providerId)}/terms`, { termsVersion });
}

export async function activateProviderProfile(providerId?: number): Promise<void> {
  await apiClient.post(`${businessPath(providerId)}/activate`);
}

export async function deactivateProviderProfile(providerId?: number): Promise<void> {
  await apiClient.post(`${businessPath(providerId)}/deactivate`);
}

export async function getMyProviderAnalytics(providerId?: number): Promise<ProviderAnalytics> {
  const response = await apiClient.get<ProviderAnalytics>(`${businessPath(providerId)}/analytics`);
  return response.data;
}

export async function recordProviderContact(
  providerId: number,
  contactMethod: ProviderContactMethod
): Promise<void> {
  await apiClient.post(`/api/v1/web/providers/${providerId}/contact`, { contactMethod });
}

export async function registerProviderProgram(
  providerId: number,
  payload: TenantProgramPublicRegistrationPayload
): Promise<TenantProgramRegistration> {
  const response = await apiClient.post<TenantProgramRegistration>(
    `/api/v1/web/providers/${providerId}/registrations`,
    payload
  );
  return response.data;
}

export async function getProviderReviews(
  providerId: number,
  page = 0,
  limit = 10
): Promise<ProviderReviewPage> {
  const response = await apiClient.get<ProviderReviewPage>(
    `/api/v1/web/providers/${providerId}/reviews?page=${page}&limit=${limit}`
  );
  return response.data;
}

export async function saveProviderReview(
  providerId: number,
  payload: SaveProviderReviewPayload,
  update: boolean
): Promise<ProviderReviewItem> {
  const url = update
    ? `/api/v1/web/providers/${providerId}/reviews/me`
    : `/api/v1/web/providers/${providerId}/reviews`;
  const response = update
    ? await apiClient.patch<ProviderReviewItem>(url, payload)
    : await apiClient.post<ProviderReviewItem>(url, payload);
  return response.data;
}

export async function deleteMyProviderReview(providerId: number): Promise<void> {
  await apiClient.delete(`/api/v1/web/providers/${providerId}/reviews/me`);
}

export async function reportProviderReview(
  providerId: number,
  reviewId: number,
  payload: ReportProviderReviewPayload
): Promise<void> {
  await apiClient.post(`/api/v1/web/providers/${providerId}/reviews/${reviewId}/report`, payload);
}
